/** Timestamps of the fuel cell data relevant for an expectation, in the order given */
type TimeSelector<T> = (data: T) => (string | null | undefined)[];

/**
 * Expectation after a command of the fuel cell: the sent values should be reported with
 * the next data. It stays open, and the user is asked to refresh later, until the fuel
 * cell reports the sent values or any newer data (by the timestamps of the fuel cell) –
 * then the user sees the current state by the timestamp. Without comparable timestamps
 * the first read resolves the expectation.
 */
export class FuelcellExpectation<T> {
  /**
   * @param isExpected Checks whether the read data already contains the sent values
   * @param getTimes Timestamps of the fuel cell relevant for the command
   * @param reference Data shown at the time of the command
   */
  constructor(
    private readonly isExpected: (data: T) => boolean,
    private readonly getTimes: TimeSelector<T>,
    private readonly reference: T | undefined
  ) { }

  /** True once the data contains the sent values or is newer than the data at the time of the command */
  public isResolved(data: T): boolean {
    return this.isExpected(data) || this.isNewer(data);
  }

  /** A single newer timestamp suffices; a timestamp that cannot be compared counts as newer */
  private isNewer(data: T): boolean {
    const times = this.getTimes(data);
    const referenceTimes = this.reference ? this.getTimes(this.reference) : [];
    const pairs = times
      .map((time, index) => ({ time: time, referenceTime: referenceTimes[index] }))
      .filter(pair => pair.time != null || pair.referenceTime != null);
    if (pairs.length < 1) { return true; }
    return pairs.some(pair => pair.time == null || pair.referenceTime == null
      || new Date(pair.time).getTime() > new Date(pair.referenceTime).getTime());
  }
}
